/**
 * VM count and the "created vs attached" topology summary.
 *
 * Revision 3: the deployment model decides both servers, so a request creates
 * one VM (SaaS-Hybrid) or three (Dedicated), and every created server has the
 * step-2 size (R3-G). VM sizes come from the admin T-shirt sizes of the
 * `workwise` product, passed in as `SizeInfo`.
 * Revision 7: prices are no longer computed here. The estimate comes from the
 * backend (POST /workwise/cost-estimate/, see costEstimate.ts).
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { DB_ENGINE_VERSION } from '../../config/workwise'
import type { ExistingNetwork, SharedServer, WorkwiseFormValues, WorkwiseRole, WorkwiseSku } from '../../types/workwise'
import { vmCountOf } from './model'
import { sharedServerDetail } from './sharedServers'

export interface SharedPicks {
  db?: SharedServer | null
  gateway?: SharedServer | null
  /** Revision 4: the existing network a SaaS-Hybrid app server joins. */
  network?: ExistingNetwork | null
}

/** How the caller resolves a T-shirt size into the admin VM size. */
export interface SizeInfo {
  vmSizeFor: (sku: WorkwiseSku) => string | null
}

const NO_SIZE_INFO: SizeInfo = { vmSizeFor: () => null }

export interface TopologyServer {
  role: WorkwiseRole
  /** VM name for created servers, registry name for attached ones. */
  name: string
  detail: string
  kind: 'created' | 'attached'
}

/** The app server is always created; the DB and gateway too when the model is Dedicated. */
export function vmCount(values: Pick<WorkwiseFormValues, 'deploymentModel'>): number {
  return vmCountOf(values.deploymentModel)
}

function sizeLabel(sku: WorkwiseSku, sizes: SizeInfo): string {
  const vm = sizes.vmSizeFor(sku)
  return vm ? `${sku}, ${vm}` : sku
}

export function describeTopology(values: WorkwiseFormValues, picks: SharedPicks = {}, sizes: SizeInfo = NO_SIZE_INFO) {
  const { appServer, dbServer, gatewayServer } = values
  const created: TopologyServer[] = []
  const attached: TopologyServer[] = []

  created.push({ role: 'app', name: appServer.vmName || '(name pending)', detail: sizeLabel(appServer.sku, sizes), kind: 'created' })

  if (values.deploymentModel === 'dedicated') {
    created.push({
      role: 'db',
      name: dbServer.vmName || '(name pending)',
      detail: `${sizeLabel(appServer.sku, sizes)}, SQL Server ${DB_ENGINE_VERSION}`,
      kind: 'created',
    })
    created.push({ role: 'gateway', name: gatewayServer.vmName || '(name pending)', detail: sizeLabel(appServer.sku, sizes), kind: 'created' })
  } else {
    attached.push({
      role: 'db',
      name: picks.db?.name ?? '(select a shared database server)',
      detail: picks.db ? sharedServerDetail(picks.db) : 'shared',
      kind: 'attached',
    })
    attached.push({
      role: 'gateway',
      name: picks.gateway?.name ?? '(select a shared gateway)',
      detail: picks.gateway ? sharedServerDetail(picks.gateway) : 'shared',
      kind: 'attached',
    })
  }

  return { created, attached }
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })

/** "$1,234.50". Whole-dollar rounding would hide shared pro-rata shares. */
export function formatUsd(value: number): string {
  return USD.format(Number.isFinite(value) ? value : 0)
}
